import { useCallback, useEffect, useRef, useState } from 'react';

interface UseScrollSpyOptions {
  headerOffset: number;
}

interface UseScrollSpyResult {
  activeId: string | null;
  registerSection: (id: string) => (el: HTMLElement | null) => void;
}

// Drives the reference page's sidebar highlight from scroll position. Uses a thin IntersectionObserver
// band just below the sticky header (rather than a scroll listener + getBoundingClientRect) so tall
// sections stay "active" for their whole on-screen span, not just at their top edge.
export function useScrollSpy(ids: string[], { headerOffset }: UseScrollSpyOptions): UseScrollSpyResult {
  const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);
  const elsRef = useRef(new Map<string, HTMLElement>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const registerSection = useCallback((id: string) => {
    return (el: HTMLElement | null) => {
      if (el) {
        elsRef.current.set(id, el);
        observerRef.current?.observe(el);
      } else {
        const existing = elsRef.current.get(id);
        if (existing) observerRef.current?.unobserve(existing);
        elsRef.current.delete(id);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setActiveId((prev) => {
          const hit = new Set<string>();
          for (const entry of entries) {
            if (entry.isIntersecting) hit.add((entry.target as HTMLElement).dataset.sectionId ?? '');
          }
          if (hit.size === 0) return prev;
          const order = idsRef.current;
          for (let i = order.length - 1; i >= 0; i--) {
            if (hit.has(order[i])) return order[i];
          }
          return prev;
        });
      },
      { root: null, rootMargin: `-${headerOffset}px 0px -70% 0px`, threshold: 0 }
    );
    observerRef.current = observer;
    elsRef.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [headerOffset]);

  return { activeId, registerSection };
}
